import { Dispatch, SetStateAction, useState } from 'react';
import Select, { MultiValue } from 'react-select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import SectionHeader from '@/components/FullPageCarousel/SectionHeader';
import ErrorMessage from '@/components/ErrorMessage';
import { useSignupOtherDetailsForm } from '@/redux/auth/useAuthForm';
import { IUserSignupDataWithConfirmPassword } from '@/pages/Signup';
import { useGetCNCBrandsQuery } from '@/redux/cncBrand/cncBrand.api.slice';
import { CNCBrandI } from '@/redux/cncBrand/cncBrand';
import { useLazyGetCNCModelsQuery } from '@/redux/cncModel/cncModel.api.slice';
import { useLazyGetCNCVariantsQuery } from '@/redux/cncVariant/cncVariant.api.slice';

function OtherDetails({
  formData,
  errors,
  setFormData,
}: {
  formData: IUserSignupDataWithConfirmPassword;
  errors: { [key: string]: string };
  setFormData: Dispatch<SetStateAction<IUserSignupDataWithConfirmPassword>>;
}) {
  const signupBaiscDetailsForm = useSignupOtherDetailsForm();
  const { data: cncBrandsData, isLoading: isLoadingCNCBrands } =
    useGetCNCBrandsQuery();
  const [getCNCModels] = useLazyGetCNCModelsQuery();
  const [getCNCVariants] = useLazyGetCNCVariantsQuery();

  const [cncModelOptions, setCncModelOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  const [cncVariantOptions, setCncVariantOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  const getOptionsFromCncBrands = (cncBrands: CNCBrandI[] | undefined) => {
    if (!cncBrands) return [];
    const options = cncBrands.map((brand) => ({
      label: brand.name,
      value: brand._id,
    }));
    return options;
  };

  const getOptionsFromCncVariants = async () => {
    const cncVariants = await getCNCVariants({
      modelIdList: formData.cncModel,
    }).unwrap();
    const options = cncVariants.map((variant) => ({
      label: variant.name,
      value: variant._id,
    }));
    setCncVariantOptions(options);
  };

  const handleCncBrandChange = (
    newValue: MultiValue<{ label: string; value: string }>
  ) => {
    const cncBrands = newValue.map((brand) => brand.value);

    setFormData((prev) => ({
      ...prev,
      cncBrand: cncBrands,
      cncModel: [],
      cncVariant: [],
    }));
  };

  const handleCncModelChange = (
    option: MultiValue<{ label: string; value: string } | null>
  ) => {
    setFormData((prev) => ({
      ...prev,
      cncModel: option
        .map((model) => model?.value)
        .filter((value): value is string => !!value),
      cncVariant: [],
    }));
  };

  const handleCncVariantChange = (
    newValue: MultiValue<{ label: string; value: string } | null>
  ) => {
    const cncVariants = newValue
      .map((variant) => variant?.value)
      .filter((value): value is string => !!value);
    setFormData((prev) => ({ ...prev, cncVariant: cncVariants }));
  };

  const getOptionsFromCncModels = async () => {
    const cncModels = await getCNCModels(formData.cncBrand).unwrap();
    const options = cncModels.map((model) => ({
      label: model.name,
      value: model._id,
    }));
    setCncModelOptions(options);
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Registration"
        description="Fill in the registration data. It will take a couple of minutes"
        align="left"
      />
      <div className="space-y-4">
        <Form {...signupBaiscDetailsForm}>
          <form>
            <div className="flex flex-col py-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormField
                  control={signupBaiscDetailsForm.control}
                  name="cncBrand"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CNC Brand</FormLabel>
                      <FormControl>
                        <Select
                          options={getOptionsFromCncBrands(cncBrandsData)}
                          onChange={(newValue) =>
                            handleCncBrandChange(newValue)
                          }
                          isLoading={isLoadingCNCBrands}
                          isSearchable
                          isMulti
                          name={field.name}
                        />
                      </FormControl>
                      <ErrorMessage error={errors.cncBrand} />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <FormField
                  control={signupBaiscDetailsForm.control}
                  name="cncModel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CNC Model</FormLabel>
                      <FormControl>
                        <Select
                          options={cncModelOptions}
                          onChange={(option) => handleCncModelChange(option)}
                          isLoading={isLoadingCNCBrands}
                          isSearchable
                          isMulti
                          onFocus={() => {
                            getOptionsFromCncModels();
                          }}
                          value={formData?.cncModel
                            .map((modelId) => {
                              const model = cncModelOptions?.find(
                                (m) => m.value === modelId
                              );
                              return model
                                ? { label: model.label, value: model.value }
                                : null;
                            })
                            .filter(Boolean)}
                          name={field.name}
                        />
                      </FormControl>
                      <ErrorMessage error={errors.cncModel} />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <FormField
                  control={signupBaiscDetailsForm.control}
                  name="cncVariant"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CNC Variant</FormLabel>
                      <FormControl>
                        <Select
                          options={cncVariantOptions}
                          onChange={(
                            newValue: MultiValue<{
                              label: string;
                              value: string;
                            } | null>
                          ) => handleCncVariantChange(newValue)}
                          isLoading={isLoadingCNCBrands}
                          isSearchable
                          isMulti
                          onFocus={() => {
                            getOptionsFromCncVariants();
                          }}
                          value={formData?.cncVariant
                            .map((variantId) => {
                              const variant = cncVariantOptions?.find(
                                (v) => v.value === variantId
                              );
                              return variant
                                ? { label: variant.label, value: variant.value }
                                : null;
                            })
                            .filter(Boolean)}
                          name={field.name}
                        />
                      </FormControl>
                      <ErrorMessage error={errors.cncVariant} />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

export default OtherDetails;
